/**
 * Payment form — wraps Stripe's PaymentElement.
 *
 * Card data never touches our DOM; the PaymentElement renders in its
 * own iframe. We hand off the confirm-payment call to Stripe and then
 * call our own /confirm endpoint via the onSuccess callback.
 */
import { useState } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

interface Props {
  onSuccess: () => Promise<void> | void;
  /** Button label. Defaults to the team-registration wording. */
  submitLabel?: string;
}

export function PaymentForm({ onSuccess, submitLabel }: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setBusy(true);
    setError(null);

    const { error: stripeErr } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // We don't redirect — Stripe handles 3DS in a popup if needed,
        // and we route to /done programmatically below.
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (stripeErr) {
      setError(stripeErr.message ?? 'Payment failed. Please try again.');
      setBusy(false);
      return;
    }

    // Stripe accepted. Tell our backend.
    try {
      await onSuccess();
    } catch {
      setError(
        'Payment succeeded but we couldn\'t mark the registration as confirmed. ' +
        'It will sync via webhook shortly.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PaymentElement />
      <button
        type="submit"
        className="tn-btn tn-btn--primary"
        disabled={!stripe || busy}
      >
        {busy ? 'Processing…' : (submitLabel ?? 'Pay and complete registration')}
      </button>
      {error ? (
        <div role="alert" style={{ color: 'var(--tn-color-danger)' }}>{error}</div>
      ) : null}
    </form>
  );
}
